import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { films } from "@/mocks/films";
import { topics } from "@/mocks/topics";
import { usePageMeta } from "@/hooks/usePageMeta";

const missions = [
  {
    icon: "ri-file-list-3-line",
    title: "资料整理",
    desc: "把散落在各处的影片信息，整理成结构统一、字段清晰的档案，方便检索与对比。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "编辑视角",
    desc: "专题片单由编辑基于时代、城市与情绪重新组合，提供比列表更有观点的观看线索。",
  },
  {
    icon: "ri-compasses-2-line",
    title: "清晰索引",
    desc: "类型、年份、地区与标签四层索引，让「想看某种感觉的片子」这件事变得可操作。",
  },
];

const scopeItems = [
  "影片基础资料：标题、日文原名、上映年份、片长、地区与画质标注",
  "主创信息：导演与主要演员阵容，用于快速判断作品风格",
  "类型与标签：剧情、爱情、悬疑、动作、经典等分类，以及情绪与场景标签",
  "剧情简介与编辑短评：帮助在打开影片之前先了解它讲的是什么",
  "相关推荐：基于类型与标签的相似影片推荐，仅作参考",
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 日本一级片",
    description: "了解日本一级片网站的定位、内容范围与使用说明，以及资料整理原则与版权声明。",
    keywords: "关于日本一级片,日本影视资料站,日本电影资讯,网站说明",
  });

  return (
    <SiteLayout>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
            <div>
              <p className="text-[11px] tracking-[0.24em] text-primary-600 uppercase mb-2 font-label">ABOUT US</p>
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight">
                关于日本一级片
              </h1>
              <p className="mt-4 text-sm md:text-[15px] leading-relaxed text-foreground-700">
                日本一级片是一个专注日本影视作品资料整理与推荐的内容站点。我们相信，找片这件事不该被杂乱的列表和夸张的标题耽误——
                清晰的年份、主创、类型与简介，才是一个影迷真正需要的东西。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                站点目前收录 {films.length} 部影片资料与 {topics.length} 个主题片单，全部为前端演示数据，
                不提供影片播放、下载或任何形式的资源分发。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "影片资料", value: films.length, unit: "部" },
                { label: "主题专题", value: topics.length, unit: "个" },
                { label: "类型标签", value: 14, unit: "类" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                  <p className="font-heading text-2xl md:text-3xl text-primary-700">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-600">{stat.unit}</p>
                  <p className="mt-2 text-[11px] text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <section>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950">定位与使命</h2>
          <p className="mt-2 text-sm text-foreground-600 max-w-[720px]">
            服务对象是关注日本影视、对影片资料有深入需求的观影爱好者与影评人。
          </p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {missions.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                </span>
                <h3 className="mt-4 text-base text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8">
          <div>
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">内容范围</h2>
            <ul className="mt-4 space-y-3">
              {scopeItems.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                  <i className="ri-checkbox-circle-line text-primary-600 mt-0.5 w-4 h-4 flex items-center justify-center shrink-0"></i>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5 h-fit">
            <h3 className="font-heading text-base text-foreground-950">联系方式</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              如对影片资料有补充或纠正，欢迎通过邮件与我们联系。当前为演示站点，邮箱地址仅作示意。
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm text-foreground-800">
              <i className="ri-mail-line text-primary-600 w-4 h-4 flex items-center justify-center"></i>
              <span className="font-label">archive@example.com</span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm text-foreground-800">
              <i className="ri-time-line text-primary-600 w-4 h-4 flex items-center justify-center"></i>
              <span>资料整理：每周一更新</span>
            </div>
            <p className="mt-4 text-xs leading-relaxed text-foreground-500">
              本站不设留言板与用户账号，所有内容均以静态资料形式呈现。
            </p>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950">使用说明与版权声明</h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="text-sm font-medium text-foreground-950">关于资料</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                站内所有影片信息、评分、点击量与评论文本均为演示样本，用于展示页面结构与交互流程，不代表任何真实作品或机构观点。
                影片名称、主创姓名与剧情内容如有雷同，纯属巧合。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="text-sm font-medium text-foreground-950">关于内容使用</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                本站仅提供影片资料的展示与索引，不提供在线播放、下载链接或任何资源分发服务。
                如需引用站内文字内容，请注明来源；图片素材仅用于页面演示。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="text-sm font-medium text-foreground-950">功能说明</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                搜索、筛选、排序、分页与相关推荐均在前端演示数据范围内运行，不连接后台接口。
                收藏、登录、支付与下载功能尚未开放。
              </p>
            </div>
            <div className="rounded-lg border border-accent-200 bg-accent-50/70 p-5">
              <h3 className="text-sm font-medium text-foreground-950">演示声明</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                本页面为产品演示用途，所有数据与功能标注均为示意。若需接入真实影片数据或内容管理系统，
                可在后续阶段单独规划。
              </p>
            </div>
          </div>
        </section>

        <div className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-lg text-foreground-950">开始浏览影片资料</h2>
            <p className="mt-1.5 text-sm text-foreground-600">从最新上映或热门推荐开始，也可以直接按类型筛选。</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/category"
              className="h-10 px-5 inline-flex items-center rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap hover:bg-primary-700 transition-colors"
            >
              进入分类页
            </Link>
            <Link
              to="/topic"
              className="h-10 px-5 inline-flex items-center rounded-md border border-background-300 text-foreground-800 text-sm whitespace-nowrap hover:border-primary-300 hover:text-primary-700 transition-colors"
            >
              查看专题片单
            </Link>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}